/**
 * AppraisalFlow.js — Appraisal contingency: enter value, view gap, pick resolution.
 */

import React, { useState, useEffect, useCallback } from 'react';
import APIAgent from '../../agents/apiAgent';

function CompactRail({ steps, currentIndex }) {
  return (
    <div className="ch-compact-rail" role="list" aria-label="Appraisal progress timeline">
      {steps.map((step, idx) => {
        const done = idx < currentIndex;
        const current = idx === currentIndex;
        return (
          <div className="ch-compact-rail-item" role="listitem" key={step.label}>
            <span className={`ch-compact-rail-dot${done ? ' is-done' : ''}${current ? ' is-current' : ''}`} aria-hidden="true">
              {done ? '✓' : idx + 1}
            </span>
            <span className={`ch-compact-rail-label${current ? ' is-current' : ''}`}>
              {step.icon ? <span className="ch-compact-rail-label-icon" aria-hidden="true">{step.icon}</span> : null}
              {step.label}
            </span>
            {idx < steps.length - 1 && <span className="ch-compact-rail-line" aria-hidden="true" />}
          </div>
        );
      })}
    </div>
  );
}

export default function AppraisalFlow({ contingency, onRefresh }) {
  const [appValue, setAppValue] = useState('');
  const [appraiserName, setAppraiserName] = useState('');
  const [appraisalDate, setAppraisalDate] = useState('');
  const [options, setOptions] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadOptions = useCallback(async () => {
    if (!contingency?.id) return;
    setLoadingOptions(true);
    try {
      const data = await APIAgent.request(`/api/contingencies/${contingency.id}/appraisal/options`);
      setOptions(data);
    } catch (err) {
      console.error('Options load error:', err);
    } finally {
      setLoadingOptions(false);
    }
  }, [contingency?.id]);

  // Load options if appraisal already entered
  useEffect(() => {
    if (contingency?.appraised_value && contingency?.status !== 'satisfied') {
      loadOptions();
    }
  }, [contingency?.appraised_value, contingency?.status, loadOptions]);

  const handleEnterAppraisal = async () => {
    if (!appValue || isNaN(parseFloat(appValue))) {
      setMsg({ type: 'error', text: 'Enter a valid appraisal value.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await APIAgent.request(`/api/contingencies/${contingency.id}/appraisal`, 'POST', {
        appraised_value: parseFloat(appValue),
        appraiser_name: appraiserName || undefined,
        appraisal_date: appraisalDate || undefined,
      });
      setMsg({ type: 'success', text: 'Appraisal value recorded.' });
      onRefresh?.();
      await loadOptions();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Failed to enter appraisal.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitResolution = async () => {
    if (!selectedOption) {
      setMsg({ type: 'error', text: 'Select a resolution option.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await APIAgent.request(`/api/contingencies/${contingency.id}/appraisal/resolve`, 'POST', {
        resolution_type: selectedOption,
        notes: notes || undefined,
        proposed_price: selectedOption === 'reduce_price' && options ? options.appraised_value : undefined,
        buyer_contribution: selectedOption === 'buyer_covers' && options ? options.gap : undefined,
      });
      setMsg({ type: 'success', text: 'Resolution proposal submitted to seller.' });
      onRefresh?.();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit resolution.' });
    } finally {
      setSaving(false);
    }
  };

  const canEnter = contingency?.status === 'active';
  const canResolve = contingency?.status === 'appraisal_entered';
  const gapDisplay = options ? options.gap : 0;

  const appraisalSteps = [
    { label: 'Received', icon: '📥' },
    { label: 'Review & Decide', icon: '🧠' },
    { label: 'Proposal Sent', icon: '📤' },
    { label: 'Resolved', icon: '✅' },
  ];
  const appraisalRailIndex =
    ['satisfied', 'removed'].includes(contingency?.status) ? 3
      : contingency?.status === 'gap_resolution_responded' ? 2
        : contingency?.status === 'gap_resolution_requested' ? 2
          : contingency?.status === 'appraisal_entered' ? 1
            : 0;

  return (
    <div>
      <div className="ch-card" style={{ padding: '0.85rem 1.05rem' }}>
        <CompactRail steps={appraisalSteps} currentIndex={appraisalRailIndex} />
      </div>

      {msg && (
        <div className={`ch-deadline-banner ${msg.type === 'error' ? 'critical' : 'low'}`}>
          {msg.text}
        </div>
      )}

      {/* Enter appraisal value */}
      {canEnter && (
        <div className="ch-card">
          <div className="ch-card-title">Enter Appraised Value</div>
          <div className="ch-form-row">
            <label className="ch-form-label">Appraised Value ($)</label>
            <input className="ch-input" type="number" min="0" placeholder="e.g. 485000" value={appValue} onChange={e => setAppValue(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div className="ch-form-row" style={{ flex: 1, minWidth: 160 }}>
              <label className="ch-form-label">Appraiser Name (optional)</label>
              <input className="ch-input" type="text" value={appraiserName} onChange={e => setAppraiserName(e.target.value)} />
            </div>
            <div className="ch-form-row" style={{ flex: 1, minWidth: 140 }}>
              <label className="ch-form-label">Appraisal Date</label>
              <input className="ch-input" type="date" value={appraisalDate} onChange={e => setAppraisalDate(e.target.value)} />
            </div>
          </div>
          <button className="ch-btn ch-btn-primary" onClick={handleEnterAppraisal} disabled={saving || !appValue}>
            {saving ? 'Saving…' : 'Save Appraisal Value'}
          </button>
        </div>
      )}

      {/* Gap summary */}
      {options && gapDisplay > 0 && (
        <div className="ch-card">
          <div className="ch-card-title">Appraisal Gap Summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
            {[
              { label: 'Offer Price', value: `$${options.offer_price?.toLocaleString()}` },
              { label: 'Appraised Value', value: `$${options.appraised_value?.toLocaleString()}` },
              { label: 'Gap', value: `$${gapDisplay?.toLocaleString()}`, highlight: true },
            ].map(item => (
              <div key={item.label} style={{ background: item.highlight ? 'var(--accent-light)' : '#f9fafb', border: `1px solid ${item.highlight ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-btn)', padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{item.label}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: item.highlight ? 'var(--accent)' : 'var(--text-primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gap satisfied — no resolution needed */}
      {contingency?.status === 'satisfied' && (
        <div className="ch-deadline-banner low" style={{ marginBottom: '1rem' }}>
          Appraisal met or exceeded offer price — contingency satisfied.
        </div>
      )}

      {/* Resolution option picker */}
      {canResolve && options?.options && (
        <div className="ch-card">
          <div className="ch-card-title">Choose a Resolution</div>
          <div className="ch-option-grid">
            {options.options.map(opt => (
              <div
                key={opt.type}
                className={`ch-option-card${selectedOption === opt.type ? ' selected' : ''}`}
                onClick={() => setSelectedOption(opt.type)}
              >
                <div className="ch-option-title">{opt.label || opt.type.replace(/_/g, ' ')}</div>
                <div className="ch-option-desc">{opt.description}</div>
                {opt.type !== 'walk_away' && (
                  <div className="ch-option-cost">
                    You pay: ${(opt.buyer_cost || 0).toLocaleString()} | Seller covers: ${(opt.seller_cost || 0).toLocaleString()}
                  </div>
                )}
                {opt.acceptance_probability != null && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                    ~{Math.round(opt.acceptance_probability * 100)}% acceptance rate
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="ch-form-row" style={{ marginTop: '1rem' }}>
            <label className="ch-form-label">Notes to seller (optional)</label>
            <textarea className="ch-textarea" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button className="ch-btn ch-btn-primary" onClick={handleSubmitResolution} disabled={saving || !selectedOption}>
            {saving ? 'Submitting…' : 'Submit Resolution Proposal'}
          </button>
        </div>
      )}

      {/* Pending seller response */}
      {contingency?.status === 'gap_resolution_requested' && (
        <div className="ch-deadline-banner medium">Waiting for seller's response to your resolution proposal.</div>
      )}

      {/* Seller responded with counter */}
      {contingency?.status === 'gap_resolution_responded' && (
        <div className="ch-deadline-banner high">
          Seller has countered your proposal. Review and decide whether to accept or renegotiate.
          {contingency.resolution_summary && (
            <div style={{ marginTop: '0.35rem', fontWeight: 400, fontSize: '0.83rem' }}>{contingency.resolution_summary}</div>
          )}
        </div>
      )}

      {loadingOptions && <div className="ch-loading">Loading options…</div>}
    </div>
  );
}
